import React, { useState, createContext, useContext } from 'react';
type Language = 'en' | 'id';
type Translations = {
  header: {
    whatIs: string;
    whoIs: string;
    whyDifferent: string;
    joinUs: string;
  };
  hero: {
    title: React.ReactNode;
    subtitle: React.ReactNode;
    description: React.ReactNode;
    registration: string;
    howItWorks: string;
    joinText: string;
  };
  comparison: {
    label: string;
    title: React.ReactNode;
    extraCost: string;
    savings: string;
    oldEcommerce: {
      badge: string;
      subtitle: string;
      ads: string;
      influencer: string;
      marketplace: string;
      platform: string;
      priceLabel: string;
      totalLabel: string;
    };
    tokoteta: {
      badge: string;
      subtitle: string;
      community: string;
      communityDesc: string;
      platform: string;
      platformDesc: string;
      userCommission: string;
      userCommissionDesc: string;
      youEarn: string;
      youEarnDesc: string;
      priceLabel: string;
      totalLabel: string;
    };
  };
  whyTokoteta: {
    label: string;
    title: string;
    cards: {
      community: {
        title: string;
        desc: string;
      };
      noAds: {
        title: string;
        desc: string;
      };
      lowFee: {
        title: string;
        desc: string;
      };
      earn: {
        title: string;
        desc: string;
      };
    };
  };
  howItWorks: {
    label: string;
    title: string;
    steps: {
      step1: {
        title: string;
        desc: string;
      };
      step2: {
        title: string;
        desc: string;
      };
      step3: {
        title: string;
        desc: string;
      };
    };
  };
  testimonials: {
    label: string;
    title: string;
    items: {
      quote: string;
      name: string;
      role: string;
    }[];
  };
  ctaBanner: {
    title: string;
    subtitle: string;
    button: string;
  };
  footer: {
    description: string;
    product: string;
    company: string;
    legal: string;
    links: {
      features: string;
      pricing: string;
      about: string;
      contact: string;
      privacy: string;
      terms: string;
    };
    rights: string;
  };
};
const translations: Record<Language, Translations> = {
  en: {
    header: {
      whatIs: 'What is Tokoteta?',
      whoIs: 'Who is Tokoteta for?',
      whyDifferent: 'Why Tokoteta is Different?',
      joinUs: 'Join Us'
    },
    hero: {
      title: <>
          Everyone Can Earn
          <br data-id="element-288" />
          With <span className="text-primary" data-id="element-289">Tokoteta</span>
        </>,
      subtitle: <>
          Share Links <span className="text-xs" data-id="element-290">♦</span> Help Neighbours{' '}
          <span className="text-xs" data-id="element-291">♦</span> Earn Together
        </>,
      description: <>
          Lower costs for sellers, better prices for buyers, and anyone can earn
          by sharing products.
          <br data-id="element-292" />
          It's{' '}
          <a href="#" className="text-primary font-semibold hover:underline" data-id="element-293">
            Tokoteta for Everyone
          </a>
          .
        </>,
      registration: '1-Minute Registration',
      howItWorks: 'How It Works',
      joinText: 'Join 10,000+ shopping and earning at Tokoteta!'
    },
    comparison: {
      label: 'THE PROBLEM WITH OLD ECOMMERCE',
      title: <>
          Buy up to 50% cheaper.
          <br data-id="element-294" />
          Recommend and earn commissions at{' '}
          <span className="text-primary" data-id="element-295">Tokoteta</span>
        </>,
      extraCost: '+ Extra Cost',
      savings: 'Save 50K!',
      oldEcommerce: {
        badge: 'OLD ECOMMERCE',
        subtitle: 'YOU PAY MORE, THEY PROFIT MORE',
        ads: 'AD COSTS',
        influencer: 'INFLUENCER FEES',
        marketplace: 'MARKETPLACE FEES',
        platform: 'PLATFORM COMMISSION',
        priceLabel: 'PRODUCT PRICE',
        totalLabel: 'TOTAL YOU PAY'
      },
      tokoteta: {
        badge: 'tt TOKOTETA',
        subtitle: 'PAY LESS, EVERYONE BENEFITS',
        community: 'COMMUNITY RECOMMENDATIONS',
        communityDesc: 'No huge ad spend needed',
        platform: 'LOWER PLATFORM FEES',
        platformDesc: 'More efficient for everyone',
        userCommission: 'DIRECT COMMISSION TO USERS',
        userCommissionDesc: 'You benefit too',
        youEarn: 'YOU EARN COMMISSIONS',
        youEarnDesc: 'On every successful recommendation',
        priceLabel: 'PRODUCT PRICE',
        totalLabel: 'TOTAL YOU PAY'
      }
    },
    whyTokoteta: {
      label: 'WHY TOKOTETA ?',
      title: 'Built for a Better Way to Commerce',
      cards: {
        community: {
          title: 'Strong Community',
          desc: 'Supportive ecosystem, sharing, learning, and growing together.'
        },
        noAds: {
          title: 'No Ads Required',
          desc: 'No need to spend on ads. Your network is your superpower.'
        },
        lowFee: {
          title: 'No Platform Fee',
          desc: 'More profit for sellers with transparent and no fees.'
        },
        earn: {
          title: 'Earn from Recommendations',
          desc: 'Anyone can share products and earn real commissions.'
        }
      }
    },
    howItWorks: {
      label: 'HOW TOKOTETA WORKS',
      title: 'Simple Steps to Share & Earn',
      steps: {
        step1: {
          title: 'Create Your Link',
          desc: 'Signup and create your link instantly.'
        },
        step2: {
          title: 'Share Everywhere',
          desc: 'Share to Whatsapp, Facebook, TikTok, or any social media.'
        },
        step3: {
          title: 'Earn Commissions',
          desc: 'Get commissions from every successful purchase.'
        }
      }
    },
    testimonials: {
      label: 'STORIES FROM TOKOTETA COMMUNITY',
      title: 'Real People. Real Results.',
      items: [{
        quote: 'I started sharing products I love in WhatsApp groups and now I earn extra income every month. Tokoteta is so easy to use!',
        name: 'Siti Nurhaliza',
        role: 'Homemaker'
      }, {
        quote: 'My sales doubled after joining Tokoteta. The community helped me reach customers I never could before.',
        name: 'Budi Santoso',
        role: 'SME Owner'
      }, {
        quote: 'The perfect side hustle while studying. I just share products with friends and earn commissions.',
        name: 'Rina Wijaya',
        role: 'College Student'
      }]
    },
    ctaBanner: {
      title: "It's Time to Shop Smarter and Earn Commissions",
      subtitle: 'Sign up free, no fees, no hassle',
      button: 'Register Now!'
    },
    footer: {
      description: 'Tokoteta connects trusted people with trusted products. Join the social commerce revolution.',
      product: 'Product',
      company: 'Company',
      legal: 'Legal',
      links: {
        features: 'Features',
        pricing: 'Pricing',
        about: 'About',
        contact: 'Contact',
        privacy: 'Privacy',
        terms: 'Terms'
      },
      rights: 'All rights reserved.'
    }
  },
  id: {
    header: {
      whatIs: 'Apa itu Tokoteta?',
      whoIs: 'Untuk Siapa Tokoteta?',
      whyDifferent: 'Kenapa Tokoteta Beda?',
      joinUs: 'Gabung Sekarang'
    },
    hero: {
      title: <>
          Semua Bisa Menghasilkan
          <br data-id="element-296" />
          Dengan <span className="text-primary" data-id="element-297">Tokoteta</span>
        </>,
      subtitle: <>
          Bagikan Link <span className="text-xs" data-id="element-298">♦</span> Bantu Tetangga{' '}
          <span className="text-xs" data-id="element-299">♦</span> Hasilkan Bersama
        </>,
      description: <>
          Biaya lebih rendah untuk penjual, harga lebih baik untuk pembeli, dan
          siapa saja bisa menghasilkan dengan membagikan produk.
          <br data-id="element-300" />
          Ini{' '}
          <a href="#" className="text-primary font-semibold hover:underline" data-id="element-301">
            Tokoteta untuk Semua
          </a>
          .
        </>,
      registration: 'Daftar 1 Menit',
      howItWorks: 'Cara Kerja',
      joinText: 'Bergabung dengan 10,000+ yang berbelanja dan menghasilkan di Tokoteta!'
    },
    comparison: {
      label: 'MASALAH DENGAN ECOMMERCE LAMA',
      title: <>
          Beli hingga 50% lebih murah.
          <br data-id="element-302" />
          Rekomendasikan dan dapatkan komisi di{' '}
          <span className="text-primary" data-id="element-303">Tokoteta</span>
        </>,
      extraCost: '+ Biaya',
      savings: 'Hemat 50K!',
      oldEcommerce: {
        badge: 'ECOMMERCE LAMA',
        subtitle: 'KAMU BAYAR LEBIH, MEREKA DIUNTUNGKAN',
        ads: 'BIAYA IKLAN',
        influencer: 'BIAYA INFLUENCER',
        marketplace: 'BIAYA MARKETPLACE',
        platform: 'KOMISI PLATFORM',
        priceLabel: 'HARGA PRODUK',
        totalLabel: 'TOTAL YANG KAMU BAYAR'
      },
      tokoteta: {
        badge: 'tt TOKOTETA',
        subtitle: 'LEBIH HEMAT, SEMUA DIUNTUNGKAN',
        community: 'REKOMENDASI DARI KOMUNITAS',
        communityDesc: '0 biaya iklan besar',
        platform: 'BIAYA PLATFORM LEBIH RENDAH',
        platformDesc: 'Lebih efisien',
        userCommission: 'KOMISI LANGSUNG UNTUK PENGGUNA',
        userCommissionDesc: 'Kamu ikut diuntungkan',
        youEarn: 'KAMU DAPAT KOMISI',
        youEarnDesc: 'Setiap rekomendasi berhasil',
        priceLabel: 'HARGA PRODUK',
        totalLabel: 'TOTAL YANG KAMU BAYAR'
      }
    },
    whyTokoteta: {
      label: 'MENGAPA TOKOTETA ?',
      title: 'Dibangun untuk Cara Berniaga yang Lebih Baik',
      cards: {
        community: {
          title: 'Komunitas Kuat',
          desc: 'Ekosistem yang mendukung, berbagi, belajar, dan tumbuh bersama.'
        },
        noAds: {
          title: 'Tanpa Iklan',
          desc: 'Tidak perlu menghabiskan uang untuk iklan. Jaringan Anda adalah kekuatan utama Anda.'
        },
        lowFee: {
          title: 'Biaya Platform Rendah',
          desc: 'Lebih banyak keuntungan bagi penjual dengan biaya yang transparan dan rendah.'
        },
        earn: {
          title: 'Hasilkan dari Rekomendasi',
          desc: 'Siapa saja dapat membagikan produk dan mendapatkan komisi nyata.'
        }
      }
    },
    howItWorks: {
      label: 'CARA KERJA TOKOTETA',
      title: 'Langkah Mudah untuk Berbagi & Menghasilkan',
      steps: {
        step1: {
          title: 'Buat Link Anda',
          desc: 'Daftar dan buat link Anda secara instan.'
        },
        step2: {
          title: 'Bagikan Dimana Saja',
          desc: 'Bagikan ke Whatsapp, Facebook, TikTok, atau media sosial apa pun.'
        },
        step3: {
          title: 'Dapatkan Komisi',
          desc: 'Dapatkan komisi dari setiap pembelian yang berhasil.'
        }
      }
    },
    testimonials: {
      label: 'CERITA DARI KOMUNITAS TOKOTETA',
      title: 'Orang Nyata. Hasil Nyata.',
      items: [{
        quote: 'Saya mulai berbagi produk yang saya suka ke grup WhatsApp dan sekarang dapat penghasilan tambahan setiap bulan. Tokoteta sangat mudah!',
        name: 'Siti Nurhaliza',
        role: 'Ibu Rumah Tangga'
      }, {
        quote: 'Penjualan saya naik 2x lipat setelah bergabung dengan Tokoteta. Komunitas membantu saya menjangkau pelanggan yang tidak pernah bisa saya capai sebelumnya.',
        name: 'Budi Santoso',
        role: 'Pemilik UMKM'
      }, {
        quote: 'Penghasilan sampingan yang sempurna sambil kuliah. Saya cukup bagikan produk ke teman-teman dan dapat komisi.',
        name: 'Rina Wijaya',
        role: 'Mahasiswa'
      }]
    },
    ctaBanner: {
      title: 'Saatnya Belanja Lebih Hemat dan Dapatkan Komisi',
      subtitle: 'Daftar gratis, tanpa biaya, tanpa ribet',
      button: 'Daftar Sekarang!'
    },
    footer: {
      description: 'Tokoteta menghubungkan orang terpercaya dengan produk terpercaya. Bergabunglah dengan revolusi social commerce.',
      product: 'Produk',
      company: 'Perusahaan',
      legal: 'Legal',
      links: {
        features: 'Fitur',
        pricing: 'Harga',
        about: 'Tentang',
        contact: 'Kontak',
        privacy: 'Privasi',
        terms: 'Syarat & Ketentuan'
      },
      rights: 'Hak cipta dilindungi.'
    }
  }
};
interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: Translations;
}
const LanguageContext = createContext<LanguageContextType | undefined>(undefined);
export function LanguageProvider({
  children
}: {
  children: ReactNode;
}) {
  const [language, setLanguage] = useState<Language>('en');
  return <LanguageContext.Provider value={{
    language,
    setLanguage,
    t: translations[language]
  }} data-id="element-304">
      {children}
    </LanguageContext.Provider>;
}
export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}